import { Action, handleActions } from 'redux-actions'
import {
  CustomType,
  ProgressType,
  TotalPointsProgress,
  UserCustomDailyProgress,
  UserDailyDiaryProgress,
  UserDailyProgress,
} from '../../../services/api.types'
import {
  addCustomEntry,
  addCustomEntryFail,
  addCustomEntrySuccess,
  getCustomProgressSuccess,
  getDailyProgressSuccess,
  getProgressSuccess,
  getTotalPointsProgressSuccess,
  resetManualEntryInProgress,
  setLastBonusPointsAdded,
  setManualEntryPointsLogicId,
  setManualEntryProgressType,
  setProgressCustomType,
  setProgressPeriod,
  setProgressType,
} from './ProgressActions'
import { ProgressPeriod } from '../../../components/YumProgressPeriod/YumProgressPeriod'
import dayjs from '../../../dayjsConfig'
import { PointsLogicId } from '../../ChallengeAdmin/components/ChallengePoints/ChallengePoints'
import { registerUserSuccess } from '../../RegisterUser/store/RegisterUserActions'

// TODO
export interface CustomProgressInnerState {
  [pointsLogicId: string]: UserCustomDailyProgress[]
}

export interface CustomProgressState {
  [goalId: string]: CustomProgressInnerState
}

export interface DailyProgressState {
  [goalId: string]: UserDailyDiaryProgress[]
}

export interface PointsProgressState {
  [goalId: string]: TotalPointsProgress[]
}

class State {
  manualEntryProgressType: CustomType = CustomType.STEPS
  manualEntryPointsLogicId: PointsLogicId | null = null
  progressType: ProgressType = ProgressType.STEPS
  customType: CustomType = CustomType.EXERCISE_MIN
  progressPeriod: ProgressPeriod = ProgressPeriod.WEEK
  progressResults: UserDailyProgress[] = []
  customProgress: CustomProgressState = {}
  dailyProgress: DailyProgressState = {}
  pointsProgress: PointsProgressState = {}
  manualEntryInProgress: boolean = false
  lastBonusPointsAdded: number | null = null
}

const convertKmToMi = (km: number) => {
  return km * 0.621371
}

const defaultCaloriesGoal = 2500
const defaultDistanceKmGoal = 8
const defaultDistanceMiGoal = 6

// Deprecated
const processProgressResults = (startDate: Date, endDate: Date, data: any, customStepsGoal?: number) => {
  const startDateDayjs = dayjs.utc(startDate)
  const endDateDayjs = dayjs.utc(endDate)

  const periodDays = endDateDayjs.diff(startDateDayjs, 'day') + 1

  // If any progress rows are missing, add placeholders.
  if (data.length < periodDays) {
    let [placeholderProgress] = data.slice(-1)
    if (!placeholderProgress) {
      const latestCaloriesGoal = localStorage.getItem('latestCaloriesGoal')
      placeholderProgress = {
        stepsGoal: customStepsGoal || 12000,
        caloriesGoal: latestCaloriesGoal ? parseInt(latestCaloriesGoal) : defaultCaloriesGoal,
        intensiveMinutesGoal: 20,
        sleepGoal: 420,
      }
    }

    const newData = []
    for (let day = dayjs(startDateDayjs); day.isSameOrBefore(endDateDayjs); day = day.add(1, 'day')) {
      const date = day.locale('en').format('YYYY-MM-DD')
      const existingData = data.find((x: { date: string }) => x.date === date)
      const dayData = existingData || {
        ...placeholderProgress,
        stepsTotal: 0,
        caloriesTotal: 0,
        intensiveMinutesTotal: 0,
        totalPercentage: 0,
        integrationTotalPercentage: 0,
        distanceKm: 0,
        distanceMi: 0,
        date,
      }
      newData.push(dayData)
    }
    data = newData
  }
  data = data.map((row: any) => {
    const r = {
      ...row,
      stepsGoal: customStepsGoal || row.stepsGoal,
      totalGoal: 100,
      distanceKmGoal: defaultDistanceKmGoal,
      distanceMiGoal: defaultDistanceMiGoal,
      distanceMi: convertKmToMi(row.distanceKm),
    }
    if (r.caloriesGoal === null) {
      r.caloriesGoal = defaultCaloriesGoal
    }
    return r
  })
  return data
}

export const processCustomDiary = (startDate: string, endDate: string, data: UserCustomDailyProgress[]) => {
  const startDateDayjs = dayjs(startDate)
  const endDateDayjs = dayjs(endDate)
  const periodDays = endDateDayjs.diff(startDateDayjs, 'day') + 1
  // Guerrilla fix to some edge case bugs.
  //startDateDayjs = dayjs(startDate).add(1, 'hour')
  if (data.length < periodDays) {
    const newData = [] as UserCustomDailyProgress[]
    for (let day = dayjs(startDateDayjs); day.isSameOrBefore(endDateDayjs); day = day.add(1, 'day')) {
      const date = day.locale('en').format('YYYY-MM-DD')
      const existingData = data.find((x: { date: string; data: any }) => x.date === date)
      newData.push(
        existingData || {
          date,
          value: 0,
          data: null,
        },
      )
    }
    data = newData
  }
  return data.map((d) => ({ ...d, data: d.data ? JSON.parse(d.data) || [] : [] }))
}

export const processDailyDiary = (startDate: string, endDate: string, data: UserDailyDiaryProgress[]) => {
  const startDateDayjs = dayjs(startDate)
  const endDateDayjs = dayjs(endDate)
  const periodDays = endDateDayjs.diff(startDateDayjs, 'day') + 1
  if (!data || data.length < periodDays) {
    const newData = [] as UserDailyDiaryProgress[]
    for (let day = dayjs(startDateDayjs); day.isSameOrBefore(endDateDayjs); day = day.add(1, 'day')) {
      const date = day.locale('en').format('YYYY-MM-DD')
      const existingData = data?.find((x: { date: string }) => x.date === date)
      newData.push(
        existingData || {
          date,
          integrationValue: 0,
          manualValue: 0,
          sum: 0,
        },
      )
    }
    data = newData
  }
  return data
}

export const progress = handleActions<State, any>(
  {
    [String(addCustomEntry)]: (state: State): State => ({
      ...state,
      manualEntryInProgress: true,
    }),
    [String(addCustomEntrySuccess)]: (state: State): State => ({
      ...state,
      manualEntryInProgress: false,
    }),
    [String(addCustomEntryFail)]: (state: State): State => ({
      ...state,
      manualEntryInProgress: false,
    }),
    [String(resetManualEntryInProgress)]: (state: State): State => ({
      ...state,
      manualEntryInProgress: false,
    }),
    [String(setManualEntryProgressType)]: (
      state: State,
      { payload: manualEntryProgressType }: Action<CustomType>,
    ): State => ({
      ...state,
      manualEntryProgressType,
    }),
    [String(setManualEntryPointsLogicId)]: (
      state: State,
      { payload: manualEntryPointsLogicId }: Action<PointsLogicId>,
    ): State => ({
      ...state,
      manualEntryPointsLogicId,
    }),
    [String(setProgressType)]: (state: State, { payload: progressType }: Action<ProgressType>): State => ({
      ...state,
      progressType,
    }),
    [String(setProgressCustomType)]: (state: State, { payload: customType }: Action<CustomType>): State => ({
      ...state,
      customType,
    }),
    [String(setProgressPeriod)]: (state: State, { payload: progressPeriod }: Action<ProgressPeriod>): State => ({
      ...state,
      progressPeriod,
    }),
    [String(getProgressSuccess)]: (
      state: State,
      { payload: { startDate, endDate, progressResults, customStepsGoal } }: Action<any>,
    ): State => ({
      ...state,
      progressResults: processProgressResults(startDate, endDate, progressResults, customStepsGoal),
    }),
    [String(getCustomProgressSuccess)]: (
      state: State,
      { payload: { goalId, startDate, endDate, customDiary, pointsLogicId } }: Action<any>,
    ): State => ({
      ...state,
      customProgress: {
        ...state.customProgress,
        [goalId]: {
          ...state.customProgress[goalId],
          [pointsLogicId || 'default']: processCustomDiary(startDate, endDate, customDiary),
        },
      },
      /*customProgress: {
        ...state.customProgress,
        [goalId]: processCustomDiary(startDate, endDate, customDiary),
      },*/
    }),
    [String(getDailyProgressSuccess)]: (
      state: State,
      { payload: { startDate, endDate, customType, dailyDiary } }: Action<any>,
    ): State => ({
      ...state,
      dailyProgress: {
        ...state.dailyProgress,
        [customType]: processDailyDiary(startDate, endDate, dailyDiary),
      },
    }),
    [String(getTotalPointsProgressSuccess)]: (
      state: State,
      { payload: { goalId, pointsProgress } }: Action<any>,
    ): State => ({
      ...state,
      pointsProgress: {
        ...state.pointsProgress,
        [goalId]: pointsProgress,
      },
    }),
    [String(setLastBonusPointsAdded)]: (state: State): State => ({
      ...state,
      lastBonusPointsAdded: new Date().getTime(),
    }),
  },
  new State(),
)

export { State as ProgressState }
